/**
 * nav-config.ts — Navigációs konfiguráció (ADR-070, ADR-071)
 * Közös forrás: Sidebar, BottomNav, NavDrawer, SidebarOverlay
 */

// ─── Types ───────────────────────────────────────────────────────────────────

export interface NavItem {
  id: string; // kebab-case azonosító, pl. 'rental', 'finance-cash'
  label: string; // i18n key (ADR-071)
  icon: string; // Heroicons SVG path string (24x24 viewBox)
  href?: string;
  external?: boolean;
  iconColor?: string; // ADR-070 modul szín
  children?: { label: string; href: string; external?: boolean }[];
}

export interface NavGroup {
  groupLabel?: string; // i18n key — ha undefined, Dashboard önálló (label nélkül)
  items: NavItem[];
}

// ─── SVG Icons (Heroicons outline 24x24) ─────────────────────────────────────

export const NAV_ICONS = {
  dashboard:
    'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
  rental: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
  worksheet:
    'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
  sales:
    'M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z',
  finance:
    'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3-.895 3-2-1.343-2-3-2zM12 8V6m0 8v6m-4-2h8M3 12a9 9 0 1118 0 9 9 0 01-18 0z',
  inventory: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4',
  manufacturing:
    'M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z',
  masterData:
    'M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4',
  partners:
    'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z',
  vehicles:
    'M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0zM13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0',
  chat: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
  porter:
    'M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4',
  messaging:
    'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
  integrations:
    'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1',
  knowledgeBase:
    'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
  reports:
    'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  tasks:
    'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
  administration:
    'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',
  settings:
    'M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4',
  more: 'M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z',
  hr: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
} as const;

// ─── Nav Items ────────────────────────────────────────────────────────────────

/** Dashboard önálló — NAV_GROUPS-on kívül a lista tetején */
export const DASHBOARD_NAV_ITEM: NavItem = {
  id: 'dashboard',
  label: 'nav.dashboard',
  icon: NAV_ICONS.dashboard,
  href: '/dashboard',
  iconColor: '#3b82f6',
};

/** NAV_GROUPS: 4 logikai csoport (F2-T1) */
export const NAV_GROUPS: NavGroup[] = [
  {
    groupLabel: 'nav.group.daily',
    items: [
      {
        id: 'rental',
        label: 'nav.rental',
        icon: NAV_ICONS.rental,
        iconColor: '#f59e0b',
        children: [
          { label: 'nav.activeRentals', href: '/rental' },
          { label: 'nav.newRental', href: '/rental/new' },
          { label: 'nav.returnRental', href: '/rental/return' },
          { label: 'nav.overdueRentals', href: '/rental/overdue' },
          { label: 'nav.fleetCalendar', href: '/rental/fleet-calendar' },
        ],
      },
      {
        id: 'worksheet',
        label: 'nav.worksheet',
        icon: NAV_ICONS.worksheet,
        iconColor: '#ef4444',
        children: [
          { label: 'nav.worksheets', href: '/worksheet' },
          { label: 'nav.newWorksheet', href: '/worksheet/new' },
          { label: 'nav.claims', href: '/worksheet/damage-claims' },
          { label: 'nav.courierDeliveries', href: '/worksheet/courier' },
        ],
      },
      {
        id: 'sales',
        label: 'nav.sales',
        icon: NAV_ICONS.sales,
        iconColor: '#10b981',
        children: [
          { label: 'nav.salesList', href: '/sales' },
          { label: 'nav.pos', href: '/sales/new' },
        ],
      },
      {
        id: 'finance',
        label: 'nav.finance',
        icon: NAV_ICONS.finance,
        iconColor: '#0891b2',
        children: [
          { label: 'nav.cashRegister', href: '/finance/cash' },
          { label: 'nav.dailyClose', href: '/finance/cash/close' },
          { label: 'nav.monthlyClose', href: '/finance/monthly-close' },
          { label: 'nav.journal', href: '/finance/journal' },
          { label: 'nav.szhFolder', href: '/finance/cash/szh' },
          { label: 'nav.vouchers', href: '/finance/cash/vouchers' },
          { label: 'nav.fixedAssets', href: '/finance/fixed-assets' },
          { label: 'nav.tamTaxExempt', href: '/finance/tam' },
          { label: 'nav.receivables', href: '/finance/receivables/aging' },
          { label: 'nav.bankImport', href: '/finance/bank-import' },
        ],
      },
    ],
  },
  {
    groupLabel: 'nav.group.registry',
    items: [
      {
        id: 'inventory',
        label: 'nav.inventory',
        icon: NAV_ICONS.inventory,
        iconColor: '#059669',
        children: [
          { label: 'nav.stockList', href: '/inventory' },
          { label: 'nav.movements', href: '/inventory/movements' },
          { label: 'nav.goodsReceipt', href: '/goods-receipt' },
          { label: 'nav.scrapping', href: '/inventory/scrap' },
          { label: 'nav.inventoryAnalytics', href: '/inventory/analytics' },
        ],
      },
      {
        id: 'manufacturing',
        label: 'nav.manufacturing',
        icon: NAV_ICONS.manufacturing,
        iconColor: '#7c3aed',
        children: [
          { label: 'nav.bomList', href: '/manufacturing/bom' },
          { label: 'nav.productionOrders', href: '/manufacturing/orders' },
        ],
      },
      {
        id: 'master-data',
        label: 'nav.masterData',
        icon: NAV_ICONS.masterData,
        iconColor: '#f97316',
        children: [
          { label: 'nav.products', href: '/products' },
          { label: 'nav.packages', href: '/packages' },
          { label: 'nav.quotes', href: '/quotations' },
          { label: 'nav.contracts', href: '/contracts' },
          { label: 'nav.invoices', href: '/invoices' },
          { label: 'nav.deliveryNotes', href: '/delivery-notes' },
        ],
      },
      {
        id: 'partners',
        label: 'nav.partners',
        icon: NAV_ICONS.partners,
        iconColor: '#8b5cf6',
        children: [
          { label: 'nav.partnerList', href: '/partners' },
          { label: 'nav.newPartner', href: '/partners/new' },
          { label: 'nav.vipProgram', href: '/partners/loyalty' },
          { label: 'nav.duplicates', href: '/partners/duplicates' },
        ],
      },
      {
        id: 'vehicles',
        label: 'nav.vehicles',
        icon: NAV_ICONS.vehicles,
        iconColor: '#06b6d4',
        children: [
          { label: 'nav.rentalVehicles', href: '/vehicles/rental' },
          { label: 'nav.companyVehicles', href: '/vehicles/company' },
          { label: 'nav.expiringDocuments', href: '/vehicles/expiring' },
        ],
      },
    ],
  },
  {
    groupLabel: 'nav.group.communication',
    items: [
      {
        id: 'porter',
        label: 'nav.porter',
        icon: NAV_ICONS.porter,
        iconColor: '#14B8A6',
        children: [
          { label: 'nav.porterInbox', href: '/porter/inbox' },
          { label: 'nav.porterChannels', href: '/porter/settings/channels' },
          { label: 'nav.porterAutoReply', href: '/porter/settings/auto-reply' },
        ],
      },
      {
        id: 'chat',
        label: 'nav.chat',
        icon: NAV_ICONS.chat,
        href: '/chat',
        iconColor: '#22c55e',
      },
      {
        id: 'messaging',
        label: 'nav.messaging',
        icon: NAV_ICONS.messaging,
        iconColor: '#d946ef',
        children: [
          { label: 'nav.smsDashboard', href: '/messaging/sms' },
          { label: 'nav.inboundSms', href: '/messaging/sms/inbound' },
          { label: 'nav.bulkEmail', href: '/messaging/bulk-email' },
        ],
      },
      {
        id: 'integrations',
        label: 'nav.integrations',
        icon: NAV_ICONS.integrations,
        iconColor: '#0ea5e9',
        children: [
          { label: 'nav.crm', href: '/integrations/crm' },
          { label: 'nav.flow', href: '/integrations/flow' },
          { label: 'nav.crmNewWindow', href: 'https://crm-demo.mflerp.com', external: true },
        ],
      },
      {
        id: 'knowledge-base',
        label: 'nav.knowledgeBase',
        icon: NAV_ICONS.knowledgeBase,
        href: '/admin-center/kb',
        iconColor: '#ca8a04',
      },
    ],
  },
  {
    groupLabel: 'nav.group.hr',
    items: [
      {
        id: 'hr',
        label: 'nav.hrModule',
        icon: NAV_ICONS.hr,
        iconColor: '#0891b2',
        children: [
          { label: 'nav.hrAttendance', href: '/hr/attendance' },
          { label: 'nav.hrLeaveCalendar', href: '/hr/leave/calendar' },
          { label: 'nav.hrLeaveRequest', href: '/hr/leave/request' },
          { label: 'nav.hrEmployees', href: '/hr/employees' },
        ],
      },
    ],
  },
  {
    groupLabel: 'nav.group.system',
    items: [
      {
        id: 'reports',
        label: 'nav.reports',
        icon: NAV_ICONS.reports,
        href: '/reports',
        iconColor: '#ec4899',
      },
      {
        id: 'tasks',
        label: 'nav.tasks',
        icon: NAV_ICONS.tasks,
        href: '/tasks',
        iconColor: '#a855f7',
      },
      {
        id: 'settings',
        label: 'nav.settings',
        icon: NAV_ICONS.settings,
        iconColor: '#78716c',
        children: [
          { label: 'nav.general', href: '/settings' },
          { label: 'nav.openingHours', href: '/settings/business-hours' },
          { label: 'nav.emailTemplates', href: '/settings/email-templates' },
          { label: 'nav.emailSmtp', href: '/settings/email-smtp' },
          { label: 'nav.emailLogs', href: '/settings/email-logs' },
          { label: 'nav.smsSettings', href: '/settings/sms' },
          { label: 'nav.documentScanner', href: '/settings/ocr' },
        ],
      },
      {
        // Admin Központ + Admin összevonva (F2-T1, AC-2)
        id: 'administration',
        label: 'nav.administration',
        icon: NAV_ICONS.administration,
        iconColor: '#475569',
        children: [
          { label: 'nav.summary', href: '/admin-center' },
          { label: 'nav.users', href: '/users' },
          { label: 'nav.permissions', href: '/users/roles' },
          { label: 'nav.tenantAdmin', href: '/tenant' },
          { label: 'nav.featureFlags', href: '/feature-flags' },
          { label: 'nav.releaseHub', href: '/admin-center/release-hub' },
        ],
      },
    ],
  },
];

/** Összes nav item lapos tömbben (favorites, breadcrumb lookup) */
export const FLAT_NAV_ITEMS: (NavItem & { group?: string })[] = [
  DASHBOARD_NAV_ITEM,
  ...NAV_GROUPS.flatMap(group =>
    group.items.flatMap(item => {
      // F3 fix: ne másoljuk a szülő children tömbjét a child item-ekbe
      const { children: _ch, ...itemWithoutChildren } = item;
      void _ch;
      return item.children
        ? item.children.map(child => ({
            ...itemWithoutChildren,
            ...child,
            group: group.groupLabel,
          }))
        : [{ ...itemWithoutChildren, group: group.groupLabel }];
    })
  ),
];

// ─── Bottom Nav Tabs (F3-T5) ─────────────────────────────────────────────────

export const BOTTOM_NAV_HEIGHT = 56; // px — NavDrawer bottom offset

export type BottomNavAction = 'openDrawer';

export interface BottomNavTab {
  id: string;
  label: string; // i18n key
  icon: string; // SVG path
  iconColor: string;
  href?: string;
  action?: BottomNavAction;
}

export const BOTTOM_NAV_TABS: BottomNavTab[] = [
  {
    id: 'dashboard',
    label: 'nav.dashboard',
    icon: NAV_ICONS.dashboard,
    iconColor: '#3b82f6',
    href: '/dashboard',
  },
  {
    id: 'rental',
    label: 'nav.rental',
    icon: NAV_ICONS.rental,
    iconColor: '#f59e0b',
    href: '/rental',
  },
  {
    id: 'worksheet',
    label: 'nav.worksheet',
    icon: NAV_ICONS.worksheet,
    iconColor: '#ef4444',
    href: '/worksheet',
  },
  {
    id: 'finance',
    label: 'nav.finance',
    icon: NAV_ICONS.finance,
    iconColor: '#14b8a6',
    href: '/finance/cash',
  },
  {
    id: 'more',
    label: 'nav.more',
    icon: NAV_ICONS.more,
    iconColor: '#64748b',
    action: 'openDrawer',
  },
];

// ─── Wizard Routes (F1-T2, F3-T3) ────────────────────────────────────────────

/** Pontos wizard route lista — NEM regex. AppLayout és BottomNav is ezt használja. */
export const WIZARD_ROUTES = ['/rental/new', '/worksheet/new'] as const;

// ─── Breadcrumb Map (F2-T3) ──────────────────────────────────────────────────

/** pathname szegmens → i18n key mapping (pótol route handle meta-t) */
export const BREADCRUMB_MAP: Record<string, string> = {
  dashboard: 'nav.dashboard',
  rental: 'nav.rental',
  worksheet: 'nav.worksheet',
  sales: 'nav.sales',
  finance: 'nav.finance',
  inventory: 'nav.inventory',
  manufacturing: 'nav.manufacturing',
  bom: 'nav.bomList',
  orders: 'nav.productionOrders',
  packages: 'nav.packages',
  products: 'nav.products',
  quotations: 'nav.quotes',
  contracts: 'nav.contracts',
  invoices: 'nav.invoices',
  'delivery-notes': 'nav.deliveryNotes',
  'goods-receipt': 'nav.goodsReceipt',
  partners: 'nav.partners',
  vehicles: 'nav.vehicles',
  reports: 'nav.reports',
  tasks: 'nav.tasks',
  settings: 'nav.settings',
  integrations: 'nav.integrations',
  crm: 'nav.crm',
  hr: 'nav.hr',
  porter: 'nav.porter',
  flow: 'nav.flow',
  chat: 'nav.chat',
  messaging: 'nav.messaging',
  'admin-center': 'nav.administration',
  users: 'nav.users',
  tenant: 'nav.tenantAdmin',
  'feature-flags': 'nav.featureFlags',
  new: 'common.new',
  return: 'nav.returnRental',
  'damage-claims': 'nav.claims',
  courier: 'nav.courierDeliveries',
  movements: 'nav.movements',
  receive: 'nav.goodsReceipt',
  scrap: 'nav.scrapping',
  loyalty: 'nav.vipProgram',
  duplicates: 'nav.duplicates',
  company: 'nav.companyVehicles',
  expiring: 'nav.expiringDocuments',
  cash: 'nav.cashRegister',
  close: 'nav.dailyClose',
  'monthly-close': 'nav.monthlyClose',
  szh: 'nav.szhFolder',
  vouchers: 'nav.vouchers',
  'fixed-assets': 'nav.fixedAssets',
  tam: 'nav.tamTaxExempt',
  receivables: 'nav.receivables',
  aging: 'nav.receivables',
  sms: 'nav.smsDashboard',
  inbound: 'nav.inboundSms',
  roles: 'nav.permissions',
  'business-hours': 'nav.openingHours',
  ocr: 'nav.documentScanner',
  kb: 'nav.knowledgeBase',
};
